// Het assenstelsel van een onderdeel uit zijn driehoeken (O95, sinds O-120 in core). De tekening uit het
// model (core/modeltekening.ts) gebruikt het, voor de bouw-PDF en sinds O-120b ook voor de kaart in de
// viewer (modules/3d/browser/_bouwtekening.ts).
//
// De driehoeken zijn in mm, in de wereldassen van de GLB. Hier zoeken we het assenstelsel van het
// onderdeel: x langs de lengte, y langs de breedte, z langs de dikte, zoals de zaaglijst. Dat gaat zo:
// de vlakken met de grootste oppervlakte geven de kandidaat-assen, en het paar loodrechte assen waarvan
// de buitenmaten het best passen bij lengte × breedte × dikte wint. Past niets binnen 1 mm plus 3 %, dan
// null: de tekening komt dan uit het ontwerp. Gaten met een plek (O93) bepalen welke kant links en boven
// ligt; zonder gaten ligt het meeste hout links en onder, zodat een schuine kop rechts staat.
//
// De aanzichten per kijkrichting (projecteren, zichtbare en verborgen randen) waren voor de oude kaart in
// de viewer. Die tekent sinds O-120b met core/maatschets.ts, dus die code is weg.
//
// Geen three en geen DOM, zodat de tests dit in Node draaien.

export type V3 = [number, number, number];

/** Driehoeken in mm, in een willekeurig (rechtshandig) assenstelsel. */
export interface Geometrie {
  posities: ArrayLike<number>;
  indices: ArrayLike<number>;
}

/** Plek van een gat in het assenstelsel van de zaaglijst (zoals GatPlek). */
export interface GatPunt {
  x_mm: number;
  y_mm: number;
  /** Hoogte vanaf het ondervlak; alleen nodig bij een gat in een zijkant of kopse kant (O93). */
  z_mm?: number;
  diameter_mm: number;
  /** De as van de boor: 0 (x, kopse kant), 1 (y, lange zijkant) of 2 (z, boven- of ondervlak, standaard). */
  as?: 0 | 1 | 2;
}

const dot = (a: V3, b: V3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const kruis = (a: V3, b: V3): V3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const schaal = (a: V3, s: number): V3 => [a[0] * s, a[1] * s, a[2] * s];
function eenheid(a: V3): V3 | null {
  const l = Math.hypot(a[0], a[1], a[2]);
  return l > 1e-12 ? schaal(a, 1 / l) : null;
}

/** Punten samenvoegen op 0,01 mm (de GLB splitst punten per normaal) en ontaarde driehoeken weg. */
function voegSamen(geo: Geometrie): { punten: V3[]; tris: number[] } {
  const punten: V3[] = [];
  const index = new Map<string, number>();
  const nieuw: number[] = [];
  const n = Math.floor(geo.posities.length / 3);
  for (let i = 0; i < n; i++) {
    const p: V3 = [geo.posities[i * 3]!, geo.posities[i * 3 + 1]!, geo.posities[i * 3 + 2]!];
    const k = p.map((v) => Math.round(v * 100)).join(",");
    let j = index.get(k);
    if (j === undefined) {
      j = punten.length;
      index.set(k, j);
      punten.push(p);
    }
    nieuw.push(j);
  }
  const tris: number[] = [];
  for (let t = 0; t + 2 < geo.indices.length; t += 3) {
    const a = nieuw[geo.indices[t]!];
    const b = nieuw[geo.indices[t + 1]!];
    const c = nieuw[geo.indices[t + 2]!];
    if (a === undefined || b === undefined || c === undefined || a === b || b === c || a === c) continue;
    tris.push(a, b, c);
  }
  return { punten, tris };
}

/** Normaal (naar buiten) en oppervlakte per driehoek. */
function normalen(punten: V3[], tris: number[]): { n: V3[]; opp: number[] } {
  const n: V3[] = [];
  const opp: number[] = [];
  let volume = 0;
  for (let t = 0; t < tris.length; t += 3) {
    const a = punten[tris[t]!]!, b = punten[tris[t + 1]!]!, c = punten[tris[t + 2]!]!;
    const k = kruis([b[0] - a[0], b[1] - a[1], b[2] - a[2]], [c[0] - a[0], c[1] - a[1], c[2] - a[2]]);
    const l = Math.hypot(k[0], k[1], k[2]);
    n.push(l > 1e-12 ? schaal(k, 1 / l) : [0, 0, 0]);
    opp.push(l / 2);
    volume += dot(a, kruis(b, c));
  }
  // Staan de vlakken naar binnen, dan draaien we de normalen om.
  if (volume < 0) for (let i = 0; i < n.length; i++) n[i] = schaal(n[i]!, -1);
  return { n, opp };
}

/** Richtingen van de grootste vlakken, zonder teken (n en -n zijn één richting). */
function hoofdrichtingen(n: V3[], opp: number[]): V3[] {
  const groepen = new Map<string, { n: V3; opp: number }>();
  for (let i = 0; i < n.length; i++) {
    let r = n[i]!;
    if (opp[i]! < 1e-9) continue;
    const eerste = r.find((v) => Math.abs(v) > 1e-6) ?? 0;
    if (eerste < 0) r = schaal(r, -1);
    const k = r.map((v) => Math.round(v * 200)).join(",");
    const g = groepen.get(k);
    if (g) g.opp += opp[i]!;
    else groepen.set(k, { n: r, opp: opp[i]! });
  }
  const uit: V3[] = [];
  for (const g of [...groepen.values()].sort((a, b) => b.opp - a.opp)) {
    if (uit.some((u) => Math.abs(dot(u, g.n)) > 0.9998)) continue;
    uit.push(g.n);
    if (uit.length >= 12) break;
  }
  return uit;
}

function omvang(punten: V3[], as: V3): number {
  let min = Infinity;
  let max = -Infinity;
  for (const p of punten) {
    const w = dot(p, as);
    if (w < min) min = w;
    if (w > max) max = w;
  }
  return max - min;
}

const PERMUTATIES = [[0, 1, 2], [0, 2, 1], [1, 0, 2], [1, 2, 0], [2, 0, 1], [2, 1, 0]] as const;

/** Lengte-as en breedte-as van het onderdeel, of null als geen paar vlakken bij de maten past. */
function zoekAssen(punten: V3[], richtingen: V3[], doel: V3): { x: V3; y: V3 } | null {
  let beste: { x: V3; y: V3; score: number } | null = null;
  for (let i = 0; i < richtingen.length; i++) {
    for (let j = i + 1; j < richtingen.length; j++) {
      const a = richtingen[i]!;
      const d = dot(a, richtingen[j]!);
      if (Math.abs(d) > 0.02) continue;
      const b = eenheid([richtingen[j]![0] - a[0] * d, richtingen[j]![1] - a[1] * d, richtingen[j]![2] - a[2] * d]);
      if (!b) continue;
      const assen: V3[] = [a, b, kruis(a, b)];
      const e = assen.map((as) => omvang(punten, as));
      for (const p of PERMUTATIES) {
        // p[k] is de as die bij doel[k] hoort (lengte, breedte, dikte).
        const score = Math.max(...[0, 1, 2].map((k) => Math.abs(e[p[k]]! - doel[k]!) / (1 + 0.03 * doel[k]!)));
        if (score <= 1 && (!beste || score < beste.score - 1e-9)) beste = { x: assen[p[0]]!, y: assen[p[1]]!, score };
      }
    }
  }
  return beste ? { x: beste.x, y: beste.y } : null;
}

/** Punten in het assenstelsel van het onderdeel, met het minimum op 0. De z-as is y × x, zie boven(). */
function naarLokaal(punten: V3[], x: V3, y: V3): V3[] {
  const z = kruis(y, x);
  const lokaal = punten.map((p): V3 => [dot(p, x), dot(p, y), dot(p, z)]);
  const min: V3 = [Infinity, Infinity, Infinity];
  for (const p of lokaal) for (let k = 0; k < 3; k++) min[k] = Math.min(min[k]!, p[k]!);
  return lokaal.map((p): V3 => [p[0] - min[0], p[1] - min[1], p[2] - min[2]]);
}

/**
 * Hoeveel gaten in de geometrie terug te vinden zijn: minstens 4 punten op de rand van de cirkel,
 * gemeten loodrecht op de as van de boor.
 */
function gatenGevonden(lokaal: V3[], gaten: GatPunt[]): number {
  let gevonden = 0;
  for (const g of gaten) {
    const r = g.diameter_mm / 2;
    const as = g.as ?? 2;
    const [a, b] = as === 2 ? [0, 1] : as === 1 ? [0, 2] : [1, 2];
    const hart: V3 = [g.x_mm, g.y_mm, g.z_mm ?? 0];
    let op = 0;
    for (const p of lokaal) if (Math.abs(Math.hypot(p[a]! - hart[a]!, p[b]! - hart[b]!) - r) < 0.25) op++;
    if (op >= 4) gevonden++;
  }
  return gevonden;
}

/** Zwaartepunt van het oppervlak langs x en y. */
function zwaartepunt(lokaal: V3[], tris: number[], opp: number[]): [number, number] {
  let sx = 0, sy = 0, s = 0;
  for (let t = 0; t < tris.length; t += 3) {
    const a = opp[t / 3]!;
    for (let k = 0; k < 3; k++) {
      sx += (lokaal[tris[t + k]!]![0] * a) / 3;
      sy += (lokaal[tris[t + k]!]![1] * a) / 3;
    }
    s += a;
  }
  return s > 0 ? [sx / s, sy / s] : [0, 0];
}

/** true als a hoger scoort dan b, van voor naar achter vergeleken. */
function hoger(a: number[], b: number[]): boolean {
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return a[i]! > b[i]!;
  return false;
}

/**
 * Een onderdeel in zijn eigen assenstelsel (O-120): x langs de lengte, y langs de breedte (in het
 * bovenaanzicht naar beneden), z langs de dikte (naar de kijker in het bovenaanzicht), met het minimum op 0.
 * `normalen` wijzen naar buiten, `opp` is de oppervlakte per driehoek.
 */
export interface Lokaal {
  punten: V3[];
  /** Drie punt-indices per driehoek; punten op 0,01 mm zijn samengevoegd. */
  tris: number[];
  normalen: V3[];
  opp: number[];
  /** Buitenmaten langs x, y en z. */
  maten: V3;
  /** Hoeveel gaten uit `gaten` in de geometrie terug te vinden zijn. */
  gatenGevonden: number;
}

/**
 * Het onderdeel in zijn eigen assenstelsel, of null als de geometrie niet bij `doel` (lengte, breedte,
 * dikte) past. De kant (welke kop links, welke lange kant boven, welk vlak boven) volgt eerst de gaten,
 * dan `voorkeur` (hoger is beter, bijvoorbeeld hoe goed de omtrek past bij die uit het ontwerp), dan het
 * meeste hout links en onder.
 */
export function naarOnderdeel(geo: Geometrie, doel: V3, gaten: GatPunt[] = [], voorkeur?: (punten: V3[]) => number): Lokaal | null {
  const { punten, tris } = voegSamen(geo);
  if (tris.length < 12 || punten.length < 4) return null;
  const { n, opp } = normalen(punten, tris);
  const assen = zoekAssen(punten, hoofdrichtingen(n, opp), doel);
  if (!assen) return null;
  // Zijn maten gelijk (een vierkante lat, een vierkante plaat, een blok zo lang als dik, een kubus), dan
  // passen de assen meer kanten op. Dan beslissen de gaten welke het is (O-120): anders komt een gat in de
  // zijkant in het bovenvlak terecht. Elke volgorde van de drie assen die bij de maten past, telt mee (O-120b).
  const drie: V3[] = [assen.x, assen.y, kruis(assen.y, assen.x)];
  const varianten: Array<{ x: V3; y: V3 }> = PERMUTATIES.filter((p) => [0, 1, 2].every((k) => Math.abs(doel[k]! - doel[p[k]]!) <= 1)).map((p) => ({ x: drie[p[0]]!, y: drie[p[1]]! }));

  let keuze: { lokaal: V3[]; x: V3; y: V3; score: number[] } | null = null;
  for (const v of varianten) {
    for (const [sx, sy] of [[1, 1], [-1, 1], [1, -1], [-1, -1]] as const) {
      const x = schaal(v.x, sx);
      const y = schaal(v.y, sy);
      const lokaal = naarLokaal(punten, x, y);
      const e = [0, 1].map((k) => Math.max(...lokaal.map((p) => p[k]!)));
      const [cx, cy] = zwaartepunt(lokaal, tris, opp);
      const score = [gatenGevonden(lokaal, gaten), voorkeur ? voorkeur(lokaal) : 0, cx < e[0]! / 2 - 1e-4 * e[0]! ? 1 : 0, cy > e[1]! / 2 + 1e-4 * e[1]! ? 1 : 0];
      if (!keuze || hoger(score, keuze.score)) keuze = { lokaal, x, y, score };
    }
  }
  const { lokaal, x, y, score } = keuze!;
  // Normalen in het nieuwe assenstelsel.
  const z = kruis(y, x);
  const nLokaal = n.map((m): V3 => [dot(m, x), dot(m, y), dot(m, z)]);
  const maten: V3 = [0, 1, 2].map((k) => Math.max(...lokaal.map((p) => p[k]!))) as V3;
  return { punten: lokaal, tris, normalen: nLokaal, opp, maten, gatenGevonden: score[0]! };
}
